import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Briefcase, 
  Calendar, 
  Award, 
  ShieldCheck, 
  GraduationCap, 
  Users, 
  CheckCircle2, 
  Filter 
} from 'lucide-react';
import AdminRespo from '../../Media/23826143_10155796821610011_7920947745379610212_o.jpg';

const responsibilities = [
  { role: "Programme Coordinator, PhD", org: "DMS, IIT Delhi", period: "2025 – Present", active: true, tag: "Academic Leadership" },
  { role: "Area Chair – Strategic Management", org: "DMS, IIT Delhi", period: "2023 – Present", active: true, tag: "Departmental" },
  { role: "Theme Lead, Societal Transformations & Livelihoods", org: "UQ-IITD Research Academy", period: "2023 – Present", active: true, tag: "Institutional & Global" },
  { role: "Institute Ranking Committee", org: "DMS, IIT Delhi", period: "2022 – Present", active: true, tag: "Institutional & Global" },
  { role: "Member, Academic Innovation & Strategy Committee", org: "IIT Delhi", period: "2019 – Present", active: true, tag: "Institutional & Global" },
  { role: "Programme Coordinator, MBA (Full Time)", org: "DMS, IIT Delhi", period: "2020 – June 2025", active: false, tag: "Academic Leadership" },
  { role: "Institute Theme Lead, UQIDAR – Social Transformation", org: "UQ-IITD", period: "2024 – 2025", active: false, tag: "Institutional & Global" },
  { role: "Institute Sansthaan Awards Committee", org: "IIT Delhi", period: "2024 – 2025", active: false, tag: "Institutional & Global" },
  { role: "Disciplinary Committee Member", org: "DMS, IIT Delhi", period: "2022 – 2025", active: false, tag: "Departmental" },
  { role: "Institute Daycare Committee Chairman", org: "IIT Delhi", period: "2023 – 2024", active: false, tag: "Institutional & Global" },
  { role: "Convocation Committee Chairperson", org: "DMS, IIT Delhi", period: "2020 – 2024", active: false, tag: "Departmental" },
  { role: "Orientation Committee Member", org: "DMS, IIT Delhi", period: "2022 – 2024", active: false, tag: "Departmental" },
  { role: "Chairperson, Committee of Academic Programs (CAP)", org: "DMS, IIT Delhi", period: "2019 – 2023", active: false, tag: "Academic Leadership" },
  { role: "Chairperson, Alumni Relations", org: "DMS, IIT Delhi", period: "2018 – 2023", active: false, tag: "Departmental" },
  { role: "SIRE, DRC Committee", org: "DMS, IIT Delhi", period: "2020 – 2023", active: false, tag: "Academic Leadership" },
  { role: "JEE and GATE Institute Duty", org: "IIT Delhi", period: "2021 – 2022", active: false, tag: "Institutional & Global" },
  { role: "IoE Committee DMS Representative", org: "IIT Delhi", period: "2020 – 2021", active: false, tag: "Institutional & Global" },
  { role: "Coordinator, Media Committee", org: "DMS, IIT Delhi", period: "2015 – 2019", active: false, tag: "Departmental" },
  { role: "Chairperson, Placements Committee", org: "DMS, IIT Delhi", period: "2018 – 2019", active: false, tag: "Departmental" },
  { role: "Institute CEP Committee", org: "IIT Delhi", period: "2018 – 2019", active: false, tag: "Institutional & Global" }
];

const categories = ["All", "Academic Leadership", "Departmental", "Institutional & Global"];

export default function AdministrativeResponsibilities() {
  const [activeFilter, setActiveFilter] = useState("All");

  const filteredItems = activeFilter === "All"
    ? responsibilities
    : responsibilities.filter(r => r.tag === activeFilter);

  return (
    <div className="container py-5 min-vh-100">
      
      {/* Header Banner */}
      <div className="row align-items-center mb-5 g-4">
        <div className="col-lg-7" data-aos="fade-right">
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3 sub-badge">
            <ShieldCheck size={16} className="text-cyan" />
            <span className="small text-light fw-medium">Institutional Governance</span>
          </div>
          <h1 className="display-5 fw-bold text-white mb-3">
            Administrative <span className="text-gradient">Responsibilities</span>
          </h1>
          <p className="text-secondary lead fs-6 mb-4">
            Key administrative leadership appointments, academic governance, and institutional committee assignments across IIT Delhi and international research alliances.
          </p>

          {/* Quick Metrics Bar */}
          <div className="d-flex gap-3">
            <div className="glass-card-sm px-3 py-2 rounded-3">
              <span className="fs-5 fw-bold text-cyan">5+</span>
              <span className="small text-secondary d-block">Active Tenures</span>
            </div>
            <div className="glass-card-sm px-3 py-2 rounded-3">
              <span className="fs-5 fw-bold text-warning">20+</span>
              <span className="small text-secondary d-block">Total Roles</span>
            </div>
          </div>
        </div>

        <div className="col-lg-5" data-aos="fade-left">
          <div className="position-relative p-2 rounded-4 glass-panel border-glow">
            <img 
              src={AdminRespo} 
              alt="Administrative Office" 
              className="img-fluid rounded-4 shadow w-100" 
              style={{ maxHeight: '280px', objectFit: 'cover' }} 
            />
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="d-flex flex-wrap gap-2 mb-4 pb-2 border-bottom border-secondary border-opacity-25">
        <div className="d-flex align-items-center gap-2 text-secondary me-2 small">
          <Filter size={15} /> Filter:
        </div>
        {categories.map((cat, i) => (
          <button
            key={i}
            onClick={() => setActiveFilter(cat)}
            className={`btn btn-sm rounded-pill px-3 py-1 border transition-all ${
              activeFilter === cat 
                ? 'btn-gradient text-dark fw-bold' 
                : 'btn-outline-glass text-secondary'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Timeline Stream */}
      <div className="row g-3">
        {filteredItems.map((item, idx) => (
          <div className="col-12 col-md-6" key={idx} data-aos="fade-up" data-aos-delay={idx * 35}>
            <motion.div 
              whileHover={{ y: -3 }}
              className="p-3 rounded-4 glass-panel h-100 d-flex flex-column justify-content-between position-relative overflow-hidden"
              style={{
                borderLeft: item.active 
                  ? '4px solid #00f2fe' 
                  : '4px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className={`badge px-2 py-1 rounded-pill small ${
                    item.active 
                      ? 'bg-info bg-opacity-10 text-cyan border border-info border-opacity-25' 
                      : 'bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25'
                  }`}>
                    {item.tag}
                  </span>
                  
                  <span className="small text-secondary d-flex align-items-center gap-1">
                    <Calendar size={13} /> {item.period}
                  </span>
                </div>

                <h5 className="fw-bold text-white fs-6 mb-1">{item.role}</h5>
                <p className="text-secondary small mb-0">{item.org}</p>
              </div>

              {item.active && (
                <div className="mt-3 pt-2 border-top border-secondary border-opacity-10 d-flex align-items-center gap-1 text-cyan small fw-semibold">
                  <CheckCircle2 size={13} /> Currently Active
                </div>
              )}
            </motion.div>
          </div>
        ))}
      </div>

    </div>
  );
}